/**
 * Advanced IndexedDB cache for Google Drive API responses
 * Features: ETag support, compression, efficient querying, analytics
 */

class IndexedDBCache {
  constructor(options = {}) {
    this.dbName = options.dbName || 'GoogleDriveCache';
    this.version = options.version || 1;
    this.db = null;
    this.maxCacheSize = options.maxCacheSize || 100 * 1024 * 1024; // 100MB
    this.compressionThreshold = options.compressionThreshold || 1024; // 1KB
    this.defaultTTL = options.defaultTTL || 30 * 60 * 1000; // 30 minutes
    
    // Cache analytics
    this.analytics = {
      hits: 0,
      misses: 0,
      writes: 0,
      errors: 0
    };
  }

  /**
   * Initialize the IndexedDB database
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      
      request.onerror = () => {
        this.analytics.errors++;
        reject(new Error(`Failed to open database: ${request.error?.message}`));
      };
      
      request.onsuccess = () => {
        this.db = request.result;
        this.setupErrorHandling();
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        this.createObjectStores(db);
      };
    });
  }

  /**
   * Create object stores and indexes
   */
  createObjectStores(db) {
    // Files store with comprehensive indexing
    if (!db.objectStoreNames.contains('files')) {
      const filesStore = db.createObjectStore('files', { keyPath: 'id' });
      
      // Indexes for efficient querying
      filesStore.createIndex('name', 'name');
      filesStore.createIndex('mimeType', 'mimeType');
      filesStore.createIndex('parentId', 'parents', { multiEntry: true });
      filesStore.createIndex('modifiedTime', 'modifiedTime');
      filesStore.createIndex('cachedAt', 'metadata.cachedAt');
      filesStore.createIndex('etag', 'metadata.etag');
      filesStore.createIndex('size', 'size');
    }

    // API responses cache
    if (!db.objectStoreNames.contains('apiResponses')) {
      const apiStore = db.createObjectStore('apiResponses', { keyPath: 'cacheKey' });
      apiStore.createIndex('url', 'url');
      apiStore.createIndex('method', 'method');
      apiStore.createIndex('timestamp', 'timestamp');
      apiStore.createIndex('ttl', 'ttl');
      apiStore.createIndex('etag', 'etag');
    }

    // Search index for full-text search
    if (!db.objectStoreNames.contains('searchIndex')) {
      const searchStore = db.createObjectStore('searchIndex', { keyPath: 'term' });
      searchStore.createIndex('fileIds', 'fileIds', { multiEntry: true });
      searchStore.createIndex('frequency', 'frequency');
    }

    // User preferences and settings
    if (!db.objectStoreNames.contains('userPreferences')) {
      db.createObjectStore('userPreferences', { keyPath: 'key' });
    }

    // Cache analytics and metadata
    if (!db.objectStoreNames.contains('cacheMetadata')) {
      const metaStore = db.createObjectStore('cacheMetadata', { keyPath: 'id' });
      metaStore.createIndex('type', 'type');
      metaStore.createIndex('timestamp', 'timestamp');
    }
  }

  /**
   * Setup error handling for the database
   */
  setupErrorHandling() {
    this.db.onerror = (event) => {
      console.error('Database error:', event.target.error);
      this.analytics.errors++;
    };

    this.db.onabort = (event) => {
      console.warn('Database transaction aborted:', event);
    };
  }

  /**
   * Cache a file with metadata and ETag support
   */
  async cacheFile(file, etag = null, ttl = null) {
    try {
      const transaction = this.db.transaction(['files'], 'readwrite');
      const store = transaction.objectStore('files');

      const cacheEntry = {
        ...file,
        metadata: {
          cachedAt: Date.now(),
          etag: etag,
          ttl: ttl || this.defaultTTL,
          compressed: false,
          originalSize: JSON.stringify(file).length
        }
      };

      // Compress large entries
      if (cacheEntry.metadata.originalSize > this.compressionThreshold) {
        cacheEntry.compressedData = await this.compressData(file);
        cacheEntry.metadata.compressed = true;
        // Remove original data to save space
        Object.keys(file).forEach(key => {
          if (key !== 'id') delete cacheEntry[key];
        });
      }

      await this.putData(store, cacheEntry);
      
      // Update search index
      await this.updateSearchIndex(file);
      
      this.analytics.writes++;
      return true;
    } catch (error) {
      console.error('Failed to cache file:', error);
      this.analytics.errors++;
      return false;
    }
  }

  /**
   * Get cached file with automatic decompression
   */
  async getCachedFile(fileId) {
    try {
      const transaction = this.db.transaction(['files'], 'readonly');
      const store = transaction.objectStore('files');
      
      const cachedEntry = await this.getData(store, fileId);
      
      if (!cachedEntry) {
        this.analytics.misses++;
        return null;
      }

      // Check TTL
      const age = Date.now() - cachedEntry.metadata.cachedAt;
      if (age > cachedEntry.metadata.ttl) {
        // Expired - remove and return null
        await this.removeFile(fileId);
        this.analytics.misses++;
        return null;
      }

      this.analytics.hits++;

      // Decompress if needed
      if (cachedEntry.metadata.compressed) {
        const decompressedData = await this.decompressData(cachedEntry.compressedData);
        return {
          ...decompressedData,
          id: cachedEntry.id,
          metadata: cachedEntry.metadata
        };
      }

      return cachedEntry;
    } catch (error) {
      console.error('Failed to get cached file:', error);
      this.analytics.errors++;
      return null;
    }
  }

  /**
   * Cache API response with ETag support
   */
  async cacheAPIResponse(url, method, response, etag = null, ttl = null) {
    try {
      const cacheKey = this.generateCacheKey(url, method);
      const transaction = this.db.transaction(['apiResponses'], 'readwrite');
      const store = transaction.objectStore('apiResponses');

      const cacheEntry = {
        cacheKey,
        url,
        method,
        response: await this.compressData(response),
        etag,
        timestamp: Date.now(),
        ttl: ttl || this.defaultTTL,
        size: JSON.stringify(response).length
      };

      await this.putData(store, cacheEntry);
      this.analytics.writes++;
      return true;
    } catch (error) {
      console.error('Failed to cache API response:', error);
      this.analytics.errors++;
      return false;
    }
  }

  /**
   * Get cached API response
   */
  async getCachedAPIResponse(url, method) {
    try {
      const cacheKey = this.generateCacheKey(url, method);
      const transaction = this.db.transaction(['apiResponses'], 'readonly');
      const store = transaction.objectStore('apiResponses');
      
      const cachedEntry = await this.getData(store, cacheKey);
      
      if (!cachedEntry) {
        this.analytics.misses++;
        return null;
      }

      // Check TTL
      const age = Date.now() - cachedEntry.timestamp;
      if (age > cachedEntry.ttl) {
        await this.removeAPIResponse(cacheKey);
        this.analytics.misses++;
        return null;
      }

      this.analytics.hits++;
      
      return {
        response: await this.decompressData(cachedEntry.response),
        etag: cachedEntry.etag,
        timestamp: cachedEntry.timestamp
      };
    } catch (error) {
      console.error('Failed to get cached API response:', error);
      this.analytics.errors++;
      return null;
    }
  }

  /**
   * Advanced query with multiple filters
   */
  async queryFiles(filters = {}) {
    try {
      const transaction = this.db.transaction(['files'], 'readonly');
      const store = transaction.objectStore('files');
      
      let results = [];

      if (filters.parentId) {
        const index = store.index('parentId');
        results = await this.getAllFromIndex(index, filters.parentId);
      } else if (filters.mimeType) {
        const index = store.index('mimeType');
        results = await this.getAllFromIndex(index, filters.mimeType);
      } else if (filters.name) {
        // Perform text search
        results = await this.searchFiles(filters.name);
      } else {
        results = await this.getAllFromStore(store);
      }

      // Apply additional filters
      if (filters.modifiedAfter) {
        results = results.filter(file => 
          new Date(file.modifiedTime) > filters.modifiedAfter
        );
      }

      if (filters.sizeMin || filters.sizeMax) {
        results = results.filter(file => {
          const size = file.size || 0;
          return (!filters.sizeMin || size >= filters.sizeMin) &&
                 (!filters.sizeMax || size <= filters.sizeMax);
        });
      }

      // Sort results
      if (filters.sortBy) {
        results.sort((a, b) => {
          const aVal = a[filters.sortBy];
          const bVal = b[filters.sortBy];
          
          if (filters.sortOrder === 'desc') {
            return bVal > aVal ? 1 : -1;
          }
          return aVal > bVal ? 1 : -1;
        });
      }

      // Pagination
      if (filters.limit || filters.offset) {
        const start = filters.offset || 0;
        const end = filters.limit ? start + filters.limit : undefined;
        results = results.slice(start, end);
      }

      return results;
    } catch (error) {
      console.error('Failed to query files:', error);
      this.analytics.errors++;
      return [];
    }
  }

  /**
   * Full-text search implementation
   */
  async searchFiles(searchTerm) {
    try {
      const transaction = this.db.transaction(['files', 'searchIndex'], 'readonly');
      const filesStore = transaction.objectStore('files');
      const searchStore = transaction.objectStore('searchIndex');
      
      const terms = searchTerm.toLowerCase().split(' ').filter(t => t.length > 2);
      const matchingFileIds = new Set();

      // Search in index first
      for (const term of terms) {
        const searchEntry = await this.getData(searchStore, term);
        if (searchEntry) {
          searchEntry.fileIds.forEach(id => matchingFileIds.add(id));
        }
      }

      // If no index matches, fall back to full scan
      if (matchingFileIds.size === 0) {
        const allFiles = await this.getAllFromStore(filesStore);
        return allFiles.filter(file => 
          file.name && file.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Get matching files
      const results = [];
      for (const fileId of matchingFileIds) {
        const file = await this.getData(filesStore, fileId);
        if (file) results.push(file);
      }

      return results;
    } catch (error) {
      console.error('Search failed:', error);
      this.analytics.errors++;
      return [];
    }
  }

  /**
   * Update search index for a file
   */
  async updateSearchIndex(file) {
    if (!file.name) return;

    try {
      const transaction = this.db.transaction(['searchIndex'], 'readwrite');
      const store = transaction.objectStore('searchIndex');
      
      const words = file.name.toLowerCase()
        .split(/\W+/)
        .filter(word => word.length > 2);

      for (const word of words) {
        let searchEntry = await this.getData(store, word);
        
        if (!searchEntry) {
          searchEntry = {
            term: word,
            fileIds: [file.id],
            frequency: 1
          };
        } else {
          if (!searchEntry.fileIds.includes(file.id)) {
            searchEntry.fileIds.push(file.id);
            searchEntry.frequency++;
          }
        }

        await this.putData(store, searchEntry);
      }
    } catch (error) {
      console.error('Failed to update search index:', error);
    }
  }

  /**
   * Batch operations for improved performance
   */
  async cacheFilesBatch(files, etags = {}) {
    const batchSize = 50;
    const batches = this.chunkArray(files, batchSize);
    
    for (const batch of batches) {
      const promises = batch.map(file => {
        const etag = etags[file.id] || null;
        return this.cacheFile(file, etag);
      });
      
      await Promise.all(promises);
      
      // Small delay to prevent blocking
      await new Promise(resolve => setTimeout(resolve, 10));
    }
  }

  /**
   * Cache cleanup and maintenance
   */
  async cleanup() {
    try {
      const now = Date.now();
      const transaction = this.db.transaction(['files', 'apiResponses'], 'readwrite');
      
      // Clean expired files
      const filesStore = transaction.objectStore('files');
      const filesCursor = await filesStore.openCursor();
      const expiredFiles = [];
      
      await this.processCursor(filesCursor, (cursor) => {
        const file = cursor.value;
        const age = now - file.metadata.cachedAt;
        
        if (age > file.metadata.ttl) {
          expiredFiles.push(file.id);
          cursor.delete();
        }
      });

      // Clean expired API responses
      const apiStore = transaction.objectStore('apiResponses');
      const apiCursor = await apiStore.openCursor();
      
      await this.processCursor(apiCursor, (cursor) => {
        const response = cursor.value;
        const age = now - response.timestamp;
        
        if (age > response.ttl) {
          cursor.delete();
        }
      });

      // Update analytics
      await this.updateCacheStats();
      
      console.log(`Cache cleanup completed. Removed ${expiredFiles.length} expired files.`);
      return expiredFiles.length;
    } catch (error) {
      console.error('Cache cleanup failed:', error);
      this.analytics.errors++;
      return 0;
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    try {
      const transaction = this.db.transaction(['files', 'apiResponses', 'cacheMetadata'], 'readonly');
      
      const filesCount = await this.getStoreCount(transaction.objectStore('files'));
      const apiCount = await this.getStoreCount(transaction.objectStore('apiResponses'));
      
      const totalSize = await this.calculateCacheSize();
      
      return {
        files: {
          count: filesCount,
          hitRate: this.analytics.hits / (this.analytics.hits + this.analytics.misses) || 0
        },
        api: {
          count: apiCount
        },
        analytics: { ...this.analytics },
        storage: {
          totalSize,
          maxSize: this.maxCacheSize,
          usagePercent: (totalSize / this.maxCacheSize) * 100
        }
      };
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      return null;
    }
  }

  // Utility methods

  generateCacheKey(url, method = 'GET') {
    return `${method}:${url}`;
  }

  async compressData(data) {
    try {
      const jsonString = JSON.stringify(data);
      const stream = new CompressionStream('gzip');
      const writer = stream.writable.getWriter();
      const reader = stream.readable.getReader();
      
      writer.write(new TextEncoder().encode(jsonString));
      writer.close();
      
      const chunks = [];
      let done = false;
      
      while (!done) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;
        if (value) chunks.push(value);
      }
      
      return new Uint8Array(chunks.reduce((acc, chunk) => [...acc, ...chunk], []));
    } catch (error) {
      // Fallback to uncompressed if compression fails
      return JSON.stringify(data);
    }
  }

  async decompressData(compressedData) {
    try {
      if (typeof compressedData === 'string') {
        return JSON.parse(compressedData);
      }
      
      const stream = new DecompressionStream('gzip');
      const writer = stream.writable.getWriter();
      const reader = stream.readable.getReader();
      
      writer.write(compressedData);
      writer.close();
      
      const chunks = [];
      let done = false;
      
      while (!done) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;
        if (value) chunks.push(value);
      }
      
      const decompressed = new Uint8Array(chunks.reduce((acc, chunk) => [...acc, ...chunk], []));
      const jsonString = new TextDecoder().decode(decompressed);
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Decompression failed:', error);
      return compressedData;
    }
  }

  chunkArray(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  async putData(store, data) {
    return new Promise((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getData(store, key) {
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllFromStore(store) {
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllFromIndex(index, key) {
    return new Promise((resolve, reject) => {
      const request = index.getAll(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async processCursor(cursor, callback) {
    return new Promise((resolve) => {
      cursor.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          callback(cursor);
          cursor.continue();
        } else {
          resolve();
        }
      };
    });
  }

  async getStoreCount(store) {
    return new Promise((resolve, reject) => {
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async calculateCacheSize() {
    // This is an approximation - IndexedDB doesn't provide exact size
    try {
      if (navigator.storage && navigator.storage.estimate) {
        const estimate = await navigator.storage.estimate();
        return estimate.usage || 0;
      }
    } catch (error) {
      console.warn('Storage estimation not available:', error);
    }
    
    return 0;
  }

  async removeFile(fileId) {
    const transaction = this.db.transaction(['files'], 'readwrite');
    const store = transaction.objectStore('files');
    return this.deleteData(store, fileId);
  }

  async removeAPIResponse(cacheKey) {
    const transaction = this.db.transaction(['apiResponses'], 'readwrite');
    const store = transaction.objectStore('apiResponses');
    return this.deleteData(store, cacheKey);
  }

  async deleteData(store, key) {
    return new Promise((resolve, reject) => {
      const request = store.delete(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updateCacheStats() {
    const stats = await this.getCacheStats();
    const transaction = this.db.transaction(['cacheMetadata'], 'readwrite');
    const store = transaction.objectStore('cacheMetadata');
    
    await this.putData(store, {
      id: 'latest_stats',
      type: 'cache_stats',
      timestamp: Date.now(),
      data: stats
    });
  }
}

export default IndexedDBCache;